import { useEffect, useState } from "react";
import { leadsApi, ApiError } from "@/api";
import type { ReportEngagement as Engagement, ReportViewSession } from "@/types";
import { Button, Spinner } from "@/components/ui";
import { prettyLabel } from "@/lib/feasibility";
import {
  SectionDwell,
  SectionOverlapFootnote,
  SessionRow,
  ago,
  stamped,
} from "@/staff/engagement/EngagementPanel";

/**
 * How the client read the feasibility report (api 326).
 *
 * The panel answers one question first — **did they open it** — because that is the one staff act
 * on. Everything else is depth behind it. The rendering primitives (dwell bars, session rows,
 * the 0-means-never timestamps) are shared with the quote editor's engagement panel and live in
 * staff/engagement/EngagementPanel.tsx; what stays here is everything report-specific — the
 * status/sent logic, the counters row, and the per-session report tags.
 *
 * Every number here is already filtered server-side: staff previews (`?preview=1`) and automated
 * fetches (link scanners, Slack unfurls, browser prefetch) are excluded from the counters, the
 * session list AND the dwell join. So the panel never needs to explain why a figure looks low —
 * what it shows is real readers. That also means a staff "Open report" click deliberately does
 * nothing here, which is the intended behaviour, not a bug to chase.
 */

/** The report-specific per-session tags — everything else in the row is shared. */
function reportTags(s: ReportViewSession) {
  const links = (s.comparable_link_clicks || 0) + (s.case_study_link_clicks || 0);
  return (
    <>
      {s.downloaded_pdf ? <span className="reng__tag">PDF</span> : null}
      {stamped(s.cta_clicked_at) ? <span className="reng__tag reng__tag--hot">CTA</span> : null}
      {stamped(s.contact_clicked_at) ? <span className="reng__tag">Contact</span> : null}
      {links > 0 ? (
        <span className="reng__tag" title="Clicked through to an LPA application record">
          {links} link{links === 1 ? "" : "s"}
        </span>
      ) : null}
    </>
  );
}

export function ReportEngagement({ leadId }: { leadId: number }) {
  const [data, setData] = useState<Engagement | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [showSessions, setShowSessions] = useState(false);
  const [marking, setMarking] = useState(false);

  /** Stamp the link as sent. Patched into local state rather than refetched: nothing else in the
   *  payload changes, and a refetch would flash the whole panel to a spinner. */
  async function markSent() {
    if (marking) return;
    setMarking(true);
    try {
      const { sent_at } = await leadsApi.markReportSent(leadId);
      setData((d) => (d && d.report ? { ...d, report: { ...d.report, sent_at } } : d));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't mark the report as sent.");
    } finally {
      setMarking(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    leadsApi
      .getReportEngagement(leadId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Couldn't load engagement.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  if (loading) return <div className="reng reng--loading"><Spinner /></div>;
  // A failure here must not imply "not opened" — say the panel is unavailable and stop.
  if (error) return <p className="reng__note" role="status">{error}</p>;
  if (!data?.report) return null;

  const r = data.report;
  const opened = r.view_count > 0;
  // Your own "Open report" clicks are preview-tagged and excluded by design, as are bots. Saying
  // how many were excluded is the difference between "the client hasn't read it" and "this panel
  // is broken" — which is exactly how a bare zero reads to someone who just opened it themselves.
  const excluded = Math.max(0, (data.total_sessions ?? 0) - data.sessions.length);
  const counters: Array<[string, number, string]> = [
    ["Opens", r.view_count, "Page loads by real readers"],
    ["PDF", r.download_count, "Downloads of the PDF"],
    ["Quote CTA", r.cta_click_count, "Clicks on the report's call to action"],
    ["Street", r.comparable_link_click_count, "Clicks through to a nearby decision (section 06)"],
    ["Cases", r.case_study_link_click_count, "Clicks through to one of our approvals (section 09)"],
  ];

  // A report can be mid-render or failed, and the counters look identical in both cases: all
  // zeros. Without this, a failed render is indistinguishable from a client who hasn't read it —
  // and the two need opposite actions. error_message is staff-only and never shown to the client.
  if (r.status === "failed") {
    return (
      <div className="reng">
        <p className="lead-drawer__warn" role="status">
          ⚠ This report failed to render, so the link shows an error page. Regenerate to fix it.
          {r.error_message ? <span className="reng__meta"> {r.error_message}</span> : null}
        </p>
      </div>
    );
  }

  return (
    <div className="reng">
      {r.status === "pending" ? (
        <p className="reng__note" role="status">
          Being rendered now — the link shows “your report is being prepared” until it lands,
          usually a couple of minutes.
        </p>
      ) : null}

      <div className={`reng__head${opened ? " reng__head--opened" : ""}`}>
        {opened ? (
          <>
            <strong>Opened {r.view_count === 1 ? "once" : `${r.view_count} times`}</strong>
            <span className="reng__meta">
              first {ago(r.first_viewed_at)} · last {ago(r.last_viewed_at)}
            </span>
          </>
        ) : (
          <>
            {/* "by the client" is the precise claim. "Not opened yet" is false to a staff reader
                who has opened it a dozen times — theirs just don't count. */}
            <strong>Not opened by the client</strong>
            {/* Sent-and-ignored versus never-sent are the same zero on the counters and call for
                opposite actions: chase them, or send it. So say which one this is. */}
            <span className="reng__meta">
              {stamped(r.sent_at)
                ? `sent ${ago(r.sent_at)} — worth a nudge`
                : "and the link has not been marked as sent"}
            </span>
          </>
        )}
      </div>

      {/* The action lives here, next to the sentence that names the gap, rather than up with
          Open / Copy / Regenerate — it is the step that follows copying the link, and this is
          where its absence is visible. */}
      <div className="reng__sent">
        {stamped(r.sent_at) ? (
          <span className="reng__meta">Link marked sent {ago(r.sent_at)}</span>
        ) : null}
        {/* Secondary while unsent, matching "Open report": a ghost button on the drawer's own
            background is nearly invisible, and this is the one thing on the panel that is an
            ACTION rather than a reading. Once it has been sent it drops to ghost, alongside Copy
            link and Regenerate — still there for a resend, no longer asking to be noticed. */}
        <Button
          variant={stamped(r.sent_at) ? "ghost" : "secondary"}
          size="sm"
          onClick={markSent}
          loading={marking}
        >
          {stamped(r.sent_at) ? "Mark sent again" : "Mark as sent"}
        </Button>
      </div>

      {excluded > 0 ? (
        <p className="reng__note reng__note--excluded">
          {excluded} staff preview{excluded === 1 ? "" : "s"} or automated fetch
          {excluded === 1 ? "" : "es"} excluded — your own “Open report” clicks carry{" "}
          <code>?preview=1</code> so they never count as the client reading it.
        </p>
      ) : null}

      <dl className="reng__counters">
        {counters.map(([label, value, hint]) => (
          <div key={label} title={hint} className={value > 0 ? "reng__counter--on" : undefined}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      {data.section_totals.length > 0 ? (
        <>
          <span className="reng__legend">Time per section</span>
          <SectionDwell totals={data.section_totals} labelFor={prettyLabel} />
          <SectionOverlapFootnote />
        </>
      ) : opened ? (
        <p className="reng__note">
          No per-section detail yet — dwell is recorded once a reader scrolls through with
          JavaScript running.
        </p>
      ) : null}

      {data.sessions.length > 0 ? (
        <div className="reng__more">
          <Button variant="ghost" size="sm" onClick={() => setShowSessions((v) => !v)}>
            {showSessions ? "Hide" : "Show"} {data.sessions.length} session
            {data.sessions.length === 1 ? "" : "s"}
          </Button>
          {showSessions ? (
            <div className="reng__sessions">
              {data.sessions.map((s) => (
                <SessionRow key={s.id} s={s} tags={reportTags(s)} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
